import type { BusinessHoursConfig } from './timeWindow';

/**
 * App-wide admin settings — a singleton Dataverse row (`cr001_app_settings`)
 * holding tenant-scoped configuration that doesn't fit any one feature
 * entity. New global settings should be added here rather than as
 * ad-hoc columns on unrelated tables.
 *
 * The backend ensures exactly one row exists; the API returns/accepts
 * a flat object keyed by camelCase setting name.
 */

export interface AppSettings {
  /**
   * Return grace period — number of days after a device's original
   * ticket was resolved during which a return check-in is exempt from
   * the diagnostics fee. Beyond this window the diagnostic line shows
   * normally; the tech can still waive it manually via the Waive
   * button (adds a `DISC.RETURN` adjustment line).
   *
   * Null / missing → no grace period (every return check-in pays the
   * diagnostic).
   */
  returnGraceDays: number | null;

  /**
   * Standard business-hours window used by time-window classification,
   * pricing rules, scheduling defaults, and T&C template variables.
   * Drives `resolveTimeWindow` (and any consumers that call into it)
   * so we have one source of truth for "when are we open."
   *
   * Null / missing → use the code default (`BUSINESS_HOURS_DEFAULT`
   * exported from `./timeWindow`).
   */
  businessHours: BusinessHoursConfig | null;

  /**
   * Catalog of product codes admins can pick when adding retail
   * options to a quote (and when filtering disti listings). Each
   * entry is a `{ code, label, deviceType?, group? }` tuple — `code`
   * is what gets stamped on the QuoteOption / DistiListing row (and
   * used as the invoice line name); `label` is what techs see in the
   * dropdown; `deviceType` (optional) tags the entry with a device
   * class (laptop / desktop / all_in_one / …) so the retail-option
   * dialog can stamp it alongside the product code without needing a
   * parallel catalog; `group` (optional) renders as a ListSubheader
   * so related items cluster together.
   *
   * Null / missing → use `PRODUCT_CODE_DEFAULTS` below.
   */
  productCodes: ProductCodeOption[] | null;

  /**
   * Quote validity window — number of days a quote stays valid after
   * it has been sent to the customer. When a quote's status transitions
   * to `sent` and the request doesn't explicitly supply a `validUntil`,
   * the backend stamps `validUntil = sent date + quoteValidityDays`.
   *
   * Null / missing → no auto-stamp; the validUntil field is left
   * untouched on send (techs can still set it manually).
   */
  quoteValidityDays: number | null;

  /**
   * The image currently pinned to the reception wall TV.
   *
   * Transient display state rather than configuration, but it lives here
   * because it is a single global value with no natural row of its own —
   * there is one wall, so there is one pin. Everything else the wall shows
   * is *derived* (the current QR is just the most recently triggered
   * session), and only a staff member's "put this up now" has nothing to
   * derive it from.
   *
   * Null / missing → the wall is showing its ad rotation. The pin is
   * cleared by the next check-in trigger or by `expiresAt`, whichever comes
   * first; an expired pin simply stops resolving and is left in storage
   * (reading wall state is a pure read and never writes).
   */
  wallPin: WallPinSetting | null;

  /**
   * Sender and subject for the check-in terms-acceptance confirmation
   * email — the message a customer receives once they have accepted the
   * T&C for their check-in session. Composed by `sm4rt-api` and sent by
   * `sm4rt_emailer`; see `scripts/checkin-confirmation-email.md` in
   * sm4rt-api.
   *
   * Null / missing (or either field null) → the built-in default subject
   * and the emailer's own `SENDER_EMAIL`, i.e. exactly the behaviour
   * before this setting existed.
   */
  checkInEmail: CheckInEmailConfig | null;

  /**
   * Whether a customer checking in via the QR flow must confirm their
   * mobile number with a one-time code before the check-in proceeds.
   *
   * Verification binds the terms acceptance to someone who has access
   * to that number — it is not a contact-detail check and not proof of
   * ownership. See `docs/mobile-otp-gate.md` in sm4rt-public-api.
   *
   * This is also the kill switch for the feature's TouchSMS dependency:
   * turning it off releases sessions that are mid-flow, because the
   * requirement is evaluated per read and never stored on a session.
   *
   * Null / missing → false (off).
   */
  checkInMobileVerification: boolean | null;

  /**
   * Maximum one-time codes that may be sent over the life of a single
   * check-in session. The 30-second resend cooldown is a delay, not a
   * cap; this is what bounds total spend per session.
   *
   * Null / missing → `CHECK_IN_OTP_CAP_DEFAULTS.perSession`.
   */
  checkInOtpMaxSendsPerSession: number | null;

  /**
   * Maximum one-time codes that may be sent to one mobile number per
   * hour, counted across all sessions. This is the control that stops
   * the endpoint being used to text a stranger repeatedly.
   *
   * Null / missing → `CHECK_IN_OTP_CAP_DEFAULTS.perNumberHour`.
   */
  checkInOtpMaxSendsPerNumberHour: number | null;

  /**
   * Maximum one-time codes that may be sent to one mobile number per
   * day, counted across all sessions. Must be >= the hourly cap, or the
   * two cannot both hold.
   *
   * Null / missing → `CHECK_IN_OTP_CAP_DEFAULTS.perNumberDay`.
   */
  checkInOtpMaxSendsPerNumberDay: number | null;

  /**
   * Wording of the one-time code message the customer receives, for both
   * channels. Read by `check_in_otp_sender` in sm4rt-api.
   *
   * Null / missing (or any field null) → `CHECK_IN_OTP_MESSAGE_DEFAULTS`,
   * i.e. exactly the hardcoded wording that preceded this setting.
   */
  checkInOtpMessage: CheckInOtpMessageConfig | null;

  /**
   * How outbound email picks its From address, display name and BCC.
   *
   * Two maps: `senders` are the identities, `bindings` point each kind of
   * message at one of them. Callers of `sm4rt_emailer` pass only a message
   * key (`invoice.send`), never an address — which sender a message uses is
   * an admin decision, so a caller naming one would quietly override it.
   *
   * Read by `sm4rt_emailer` itself rather than by each calling service, so
   * sm4rt-eset-integration (and domain_manager later) need no Dataverse
   * dependency merely to learn their own From address.
   *
   * Null / missing → the emailer's own code defaults, mirrored below as
   * `EMAIL_SENDER_DEFAULTS` / `EMAIL_BINDING_DEFAULTS`. Stored config
   * replaces those **wholesale, never per key**: with an open sender set a
   * per-key overlay would resurrect a sender an admin had deleted.
   */
  emailSending: EmailSendingConfig | null;
}

/**
 * Effective values for the OTP caps when the corresponding setting is
 * null. Mirrored in `core/services/app_settings_service.py`
 * (`_OTP_CAP_DEFAULTS`) and in sm4rt-public-api's `core/config.py` —
 * change all three together.
 *
 * A cap of 0 is deliberately NOT expressible: sm4rt-public-api's
 * `RateLimiter.allow` treats `limit <= 0` as "this dimension is
 * disabled" and permits everything, so 0 would mean unlimited rather
 * than none. The admin bounds below start at 1 for that reason.
 */
export const CHECK_IN_OTP_CAP_DEFAULTS = {
  perSession: 5,
  perNumberHour: 3,
  perNumberDay: 10,
} as const;

/** Inclusive admin bounds for the OTP caps. Enforced canonically in
 *  `app_settings_service._normalise`; the admin page mirrors them. */
export const CHECK_IN_OTP_CAP_BOUNDS = {
  perSession: { min: 1, max: 20 },
  perNumberHour: { min: 1, max: 20 },
  perNumberDay: { min: 1, max: 100 },
} as const;

/**
 * Wording of the one-time check-in code message, per channel.
 *
 * Unlike the confirmation email below — whose body is a legal artefact owned by
 * `sm4rt_emailer` — this copy is purely operational, so all of it is editable.
 *
 * Three placeholders are available, and **only** these three: an unrecognised
 * one is REJECTED on save rather than passed through. That differs from
 * `CheckInEmailConfig.subject` on purpose. A typo there costs a literal brace in
 * a subject line; a typo here can cost the code itself — `{Code}` would send the
 * customer the word "{Code}" and nothing they could type in, with no error
 * raised anywhere, and they could not finish their check-in.
 *
 * | Placeholder | Becomes |
 * |---|---|
 * | `{code}` | the one-time code — REQUIRED in `smsBody` and `emailBody` |
 * | `{company}` | `PUBLIC_COMPANY_NAME`, default `Tech Solvers` |
 * | `{minutes}` | the code's lifetime, from `CHECKIN_OTP_TTL_SECONDS` |
 *
 * `{minutes}` is derived rather than typed so the message cannot promise a
 * window different from the one sm4rt-public-api's verifier enforces. Don't
 * replace it with a hardcoded number.
 */
export interface CheckInOtpMessageConfig {
  /**
   * SMS text. Must contain `{code}`.
   *
   * Length is capped at `CHECK_IN_OTP_SMS_MAX_LENGTH`, measured AFTER the
   * placeholders are filled — that is what the customer receives and what
   * TouchSMS bills per segment.
   *
   * Null / blank → `CHECK_IN_OTP_MESSAGE_DEFAULTS.smsBody`.
   */
  smsBody: string | null;

  /**
   * Email subject. `{code}` is allowed but deliberately not required: the
   * default keeps the code out of the subject, which is the line most likely to
   * show on a lock screen or in a notification preview.
   *
   * Null / blank → `CHECK_IN_OTP_MESSAGE_DEFAULTS.emailSubject`.
   */
  emailSubject: string | null;

  /**
   * Email body, as plain text. It is HTML-escaped and split into paragraphs on
   * blank lines, so an admin cannot paste markup that changes what the message
   * appears to be or who it appears to come from.
   *
   * Must contain `{code}`.
   *
   * Null / blank → `CHECK_IN_OTP_MESSAGE_DEFAULTS.emailBody`.
   */
  emailBody: string | null;
}

/**
 * Effective wording when `checkInOtpMessage` (or one of its fields) is unset.
 * Mirrored in `core/services/check_in_otp_sender.py` — change both together.
 *
 * Both bodies name the business and say what the code is FOR, which is
 * deliberate: a bare code with no context is the shape of every phishing SMS.
 * "We will never ask you to read it out" is the anti-social-engineering line,
 * worth keeping in any rewording.
 */
export const CHECK_IN_OTP_MESSAGE_DEFAULTS = {
  smsBody:
    '{code} is your {company} check-in code. It expires in {minutes} minutes. ' +
    'We will never ask you to read it out.',
  emailSubject: 'Your {company} check-in code',
  emailBody:
    '{code} is your {company} check-in code.\n\n' +
    'It expires in {minutes} minutes. We will never ask you to read it out.',
} as const;

/** The only placeholders `checkInOtpMessage` accepts; anything else is rejected
 *  on save. Canonical here, mirrored in
 *  `app_settings_service._OTP_MESSAGE_PLACEHOLDERS`. */
export const CHECK_IN_OTP_MESSAGE_PLACEHOLDERS = [
  'code',
  'company',
  'minutes',
] as const;

/**
 * Maximum rendered SMS length in characters, enforced on save.
 *
 * Two 160-character GSM-7 segments less a margin for a longer company name or a
 * two-digit expiry. TouchSMS bills per segment, so the cap is what stops a
 * pasted paragraph quietly tripling the cost of every check-in.
 */
export const CHECK_IN_OTP_SMS_MAX_LENGTH = 300;

/**
 * Configurable pieces of the check-in confirmation email. The body is a
 * Jinja template owned by `sm4rt_emailer` (`templates/check_in_terms.html`)
 * and is deliberately NOT editable here — it is a legal artefact that
 * embeds the exact terms text the customer accepted.
 */
export interface CheckInEmailConfig {
  /**
   * Subject line. Supports the placeholders `{jobNumber}`, `{sessionNumber}`
   * and `{customerName}`; anything else is left alone rather than raising, so
   * a typo costs a literal brace in a subject, never a lost email.
   *
   * `{jobNumber}` is the RepairShopr ticket number — the reference the
   * customer quotes and a technician recognises. The email is held back until
   * it exists, so it is always resolved; a session with several devices has
   * several jobs and they are joined with ", ". `{sessionNumber}` is the
   * staff-side check-in reference and is kept only because configured
   * subjects in production still use it.
   *
   * Null / blank → `CHECK_IN_EMAIL_SUBJECT_DEFAULT`.
   */
  subject: string | null;

  /**
   * @deprecated Superseded by `emailSending`. Senders are now chosen by the
   * message's key (`checkin.terms`), so a per-flow address here would
   * override an admin's binding from somewhere they can't see.
   *
   * Must be null. A non-null value is **rejected on save** rather than
   * ignored — this field spent months in production having never applied,
   * and a second silent no-op is worse than an error message. Scheduled for
   * removal once the admin console stops offering it.
   */
  fromEmail: string | null;
}

/**
 * Default check-in confirmation subject, used when `checkInEmail.subject`
 * is unset. Mirrored in `core/services/check_in_terms_service.py`
 * (`DEFAULT_TERMS_EMAIL_SUBJECT`) — change both together.
 */
export const CHECK_IN_EMAIL_SUBJECT_DEFAULT =
  'TechSolvers Job {jobNumber} — Check-In Confirmation';

/**
 * One sender identity — an address outbound email can come from.
 */
export interface EmailSenderConfig {
  /**
   * The From address. Must sit on one of `VERIFIED_SENDING_DOMAINS`:
   * smtp2go refuses to send from an unverified domain, so an address off
   * them is rejected on save rather than discovered when every message on
   * that sender starts failing.
   */
  fromEmail: string;

  /**
   * Display name, e.g. `TechSolvers Accounts`. Composed with the address
   * into smtp2go's single `"Name <addr>"` field. Purely presentational —
   * invisible to SPF, DKIM and DMARC.
   *
   * Null → the address is sent bare.
   */
  fromName: string | null;

  /**
   * Address blind-copied on every message from this sender.
   *
   * The monitored senders BCC themselves. smtp2go has no view of the
   * mailbox and so leaves nothing in its Sent Items, which means the BCC is
   * what puts the outbound message in front of whoever is working the
   * customer's reply. Must also be on a verified domain.
   *
   * Null → no copy is kept.
   */
  bcc: string | null;

  /**
   * Whether a human reads replies to this address.
   *
   * Metadata for the admin console, not used when sending: it is what lets
   * the binding editor warn before a message whose replies matter is
   * pointed at an address nobody reads. Cannot be derived from `bcc` —
   * that is a storage choice, this is a staffing fact.
   */
  monitored: boolean;
}

/**
 * The sender registry: identities, and which kind of message uses each.
 */
export interface EmailSendingConfig {
  /**
   * Sender identities, keyed by a short name (`accounts`, `noreply`).
   * Admins may add and remove these, so the set is open.
   */
  senders: Record<string, EmailSenderConfig>;

  /**
   * Message key → sender name. Every value must be a key of `senders`, and
   * a save that breaks that is rejected: a binding pointing at a deleted
   * sender is a dangling reference, and keeping both maps in one setting is
   * what lets the backend check it without reading storage back.
   *
   * A message key with no binding falls to `fallbackSender`.
   */
  bindings: Record<string, string>;

  /**
   * Sender used for a message key no binding recognises. Must be a key of
   * `senders`. Logged loudly but still sent — an email that never goes out
   * is worse than one from a slightly wrong address.
   */
  fallbackSender: string;
}

/** Domains verified for sending. Mirrored in `sm4rt_emailer`'s
 * `Smtp2GoConfig.verified_domains` and in `app_settings_service`'s coercion —
 * change all three together. */
export const VERIFIED_SENDING_DOMAINS = [
  'techsolvers.com.au',
  'email.techsolvers.com.au',
] as const;

/**
 * Effective sender identities when `emailSending` is unset. Mirrored in
 * `sm4rt_emailer`'s `core/config.py` (`SENDER_DEFAULTS`) — change both
 * together, since that copy is the one that actually sends.
 */
export const EMAIL_SENDER_DEFAULTS: Record<string, EmailSenderConfig> = {
  noreply: {
    fromEmail: 'noreply@email.techsolvers.com.au',
    fromName: 'TechSolvers',
    bcc: null,
    monitored: false,
  },
  renewals: {
    fromEmail: 'renewals@techsolvers.com.au',
    fromName: 'TechSolvers Renewals',
    bcc: 'renewals@techsolvers.com.au',
    monitored: true,
  },
  accounts: {
    fromEmail: 'accounts@techsolvers.com.au',
    fromName: 'TechSolvers Accounts',
    bcc: 'accounts@techsolvers.com.au',
    monitored: true,
  },
  sales: {
    fromEmail: 'sales@techsolvers.com.au',
    fromName: 'TechSolvers Sales',
    bcc: 'sales@techsolvers.com.au',
    monitored: true,
  },
  status: {
    fromEmail: 'status@techsolvers.com.au',
    fromName: 'SM4RT',
    bcc: null,
    monitored: false,
  },
};

/**
 * Effective bindings when `emailSending` is unset. Mirrored in
 * `sm4rt_emailer`'s `core/config.py` (`BINDING_DEFAULTS`).
 */
export const EMAIL_BINDING_DEFAULTS: Record<string, string> = {
  'checkin.terms': 'noreply',
  'checkin.otp': 'noreply',
  'credential.link': 'noreply',
  'checkin.payment_request': 'accounts',
  'eset.renewal': 'renewals',
  'metrics.daily': 'status',
  'invoice.send': 'accounts',
  'quote.send': 'sales',
};

/** Effective `fallbackSender` when `emailSending` is unset. */
export const EMAIL_FALLBACK_SENDER = 'noreply';

/**
 * A kind of outbound email, as the admin console lists it.
 *
 * A key only does anything because some code path sends with it, so this
 * list is the catalog the binding editor renders. A key that exists in code
 * but is missing here will not appear in the UI and will resolve to
 * `fallbackSender` — so adding an email flow means adding it here too.
 */
export interface EmailMessageKeyInfo {
  key: string;
  label: string;
  description: string;
  /** Configured but nothing sends it yet — the editor should say so. */
  inert?: boolean;
  /**
   * A reply is expected in the normal course of this message, so an
   * unmonitored sender is a mistake rather than a preference and the binding
   * editor warns about it.
   *
   * Deliberately **not** set on `checkin.terms` or `credential.link`. Both
   * invite a customer to get in touch, but both were chosen to send from an
   * unmonitored address with a phone number in the body — so flagging them
   * would make the shipped defaults warn about themselves, which is how a
   * warning stops being read.
   */
  expectsReplies?: boolean;
}

export const EMAIL_MESSAGE_KEYS: EmailMessageKeyInfo[] = [
  {
    key: 'checkin.terms',
    label: 'Check-in terms confirmation',
    description:
      "The customer's confirmation that they accepted the terms for a " +
      'check-in session, carrying the exact terms text they agreed to.',
  },
  {
    key: 'checkin.otp',
    label: 'Check-in one-time code',
    description:
      'The code a customer types back to confirm their email address during ' +
      'a QR check-in.',
  },
  {
    key: 'credential.link',
    label: 'Secure credential link',
    description:
      'The one-time link a customer uses to send us a password. Security ' +
      'sensitive, so the body copy carries a phone number — a recipient who ' +
      'wants to verify it should not need to reply.',
  },
  {
    key: 'checkin.payment_request',
    label: 'Check-in payment request',
    description:
      'The diagnostics invoice, with its Xero pay link, sent when reception ' +
      'defers payment at check-in. For a new customer it says diagnostics ' +
      'start once it is paid.',
    expectsReplies: true,
  },
  {
    key: 'eset.renewal',
    label: 'ESET renewal warning',
    description:
      'Sent 40 days before an ESET subscription renews, to the billing contact.',
    expectsReplies: true,
  },
  {
    key: 'invoice.send',
    label: 'Invoice',
    description: 'Emailing an invoice to a customer.',
    inert: true,
    expectsReplies: true,
  },
  {
    key: 'quote.send',
    label: 'Quote',
    description: 'Emailing a quote to a customer.',
    inert: true,
    expectsReplies: true,
  },
  {
    key: 'metrics.daily',
    label: 'Daily metrics email (internal)',
    description:
      'The overnight status report. Internal — the only key here that never ' +
      'reaches a customer.',
  },
];

/**
 * Stored form of the wall pin. Narrower than the `WallPin` the display
 * receives (`wallDisplay.ts`), which is this plus the resolved image URL.
 */
export interface WallPinSetting {
  adId: string;
  pinnedAt: string;
  expiresAt: string;
}

export interface UpdateAppSettingsRequest {
  returnGraceDays?: number | null;
  businessHours?: BusinessHoursConfig | null;
  productCodes?: ProductCodeOption[] | null;
  quoteValidityDays?: number | null;
  wallPin?: WallPinSetting | null;
  checkInEmail?: CheckInEmailConfig | null;
  checkInOtpMessage?: CheckInOtpMessageConfig | null;
  emailSending?: EmailSendingConfig | null;
}

/**
 * One row in the admin-managed product-code catalog.
 *
 * - `code` is the stored value (e.g. `HW.PC.Laptop`). Used as a
 *   prefix when searching disti listings and as the invoice line
 *   name when the option ships, so the dotted-segment convention
 *   matters — don't change existing codes without a migration.
 * - `label` is the user-facing dropdown text.
 * - `deviceType` (optional) tags the entry with a device class
 *   (e.g. `laptop`, `desktop`, `all_in_one`). When set, the
 *   retail-option dialog stamps both `code` and `deviceType` on the
 *   new QuoteOption / DistiListing — i.e. picking a product-code
 *   entry that carries a device type also determines the device
 *   type, no separate catalog required. Leave blank for entries
 *   that aren't device-specific (services, software, etc.).
 * - `group` is an optional bucket name; the dropdown renders one
 *   `ListSubheader` per distinct group, in the order codes first
 *   appear in the array. Leave blank for ungrouped items.
 */
export interface ProductCodeOption {
  code: string;
  label: string;
  deviceType?: string;
  group?: string;
}

/**
 * Default product-code catalog used when the admin hasn't customised
 * the list. Mirrors the legacy hardcoded options the dialog shipped
 * with before settings-driven config existed.
 */
export const PRODUCT_CODE_DEFAULTS: ProductCodeOption[] = [
  { code: 'HW.PC.Laptop', label: 'Laptop', deviceType: 'laptop', group: 'Computers' },
  { code: 'HW.PC.Desktop', label: 'Desktop', deviceType: 'desktop', group: 'Computers' },
  { code: 'HW.PC.AllInOne', label: 'All-in-One', deviceType: 'all_in_one', group: 'Computers' },
];

/**
 * Catalog SKU used by the waive action on a return check-in. The line
 * gets added with a negative unitCost equal to the diagnostic line's
 * subtotal so the two cancel in the running charges total.
 */
export const DISC_RETURN_SKU = 'DISC.RETURN';

/**
 * Catalog SKU used on a repair-quote invoice to reimburse the
 * customer for the diagnostic fee they paid on the source check-in's
 * invoice. Carried through the quote in `diagnosticReimbursement`
 * and emitted as one negative line on promotion.
 */
export const DISC_DIAGS_SKU = 'DISC.Diags';
